import { useRef, useState } from 'react';
import { useQuery } from 'react-query';

import Logger from 'observability/Logger';
import VirtualSettingsAttendeeApiClient from 'lib/SplashApiClient/subclients/VirtualSettingsAttendeeApiClient';
import getSplashApiClientV2 from 'lib/SplashApiClient/singletonV2';

const LOG = new Logger('useGoldcastPolling', { level: 'info' });

export const MAX_POLLS = 10;
export const POLL_INTERVAL_MS = 3000;

// Maps snake_case backend fields to the camelCase shape GoldcastEmbed expects.
const toAttendeeConfig = (attendee) =>
    attendee
        ? {
              shortId: attendee.short_id,
              secretCode: attendee.secret_code,
              emailId: attendee.email_id,
          }
        : null;

export const useGoldcastPolling = ({ enabled, eventId, guestAccessHash }) => {
    const attemptRef = useRef(0);
    const loggedStartRef = useRef(false);
    const [isTimedOut, setIsTimedOut] = useState(false);

    const { data, status } = useQuery(
        ['goldcast-attendee-status', eventId, guestAccessHash],
        async () => {
            attemptRef.current += 1;

            if (!loggedStartRef.current) {
                loggedStartRef.current = true;
                LOG.info('Goldcast provisioning poll started', { eventId });
            }

            const client = new VirtualSettingsAttendeeApiClient(getSplashApiClientV2(), guestAccessHash);
            const response = await client.getGoldcastAttendeeStatus(eventId);
            return response?.data;
        },
        {
            enabled,
            retry: false,
            refetchIntervalInBackground: false,
            refetchInterval: (responseData) => {
                if (responseData?.attendee) return false;
                if (attemptRef.current >= MAX_POLLS) return false;
                return POLL_INTERVAL_MS;
            },
            onSuccess: (responseData) => {
                if (responseData?.attendee) {
                    LOG.info('Goldcast attendee ready after polling', {
                        eventId,
                        attempts: attemptRef.current,
                    });
                } else if (attemptRef.current >= MAX_POLLS) {
                    LOG.warn('Goldcast provisioning timed out', {
                        eventId,
                        attempts: attemptRef.current,
                    });
                    setIsTimedOut(true);
                }
            },
            onError: (error) => {
                LOG.error('Goldcast status poll failed', {
                    eventId,
                    attempt: attemptRef.current,
                    error,
                });
            },
        },
    );

    const isReady = Boolean(data?.attendee);
    const hasError = status === 'error';
    const isPolling = enabled && !isReady && !isTimedOut && !hasError;

    return {
        resolvedAttendee: isReady ? toAttendeeConfig(data.attendee) : null,
        isPolling,
        isTimedOut,
        hasError,
    };
};
